import React, { useState, useEffect, useRef } from 'react';
import {
    Clock,
    Shield,
    Zap, CheckCircle, ArrowRight, BarChart3, Upload, FileText,
    Building, CreditCard, TrendingUp, ArrowLeftRight, Layers, Workflow,
    Star, Play, Settings, Send, PieChart, RotateCcw, BatteryCharging, Link2, Bell, UserPlus,
    BarChart2, PlugZap, Ban, Map, Activity, AlertTriangle, Wallet, ShieldAlert, BarChart4, Plug,
    Users, Lock, Scale, LayoutDashboard

} from 'lucide-react';

import Footer from './footer';
import Header from './header';





export default function NETC() {
    const [animatedStats, setAnimatedStats] = useState({
        processingTime: 0,
        successRate: 0,
        dailyPayouts: 0,
        merchants: 0
    });

    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(true);
    const [animationStep, setAnimationStep] = useState(0);
    const [scrollY, setScrollY] = useState(0);
    const [isVisible, setIsVisible] = useState({});
    const [activeFeature, setActiveFeature] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [currentTitle, setCurrentTitle] = useState(0);
    const videoRef = useRef(null);
    const sectionRefs = useRef([]);

    const [currentTitleIndex, setCurrentTitleIndex] = useState(0);

    // Scroll effect
    useEffect(() => {
        const handleScroll = () => setScrollY(window.scrollY);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Intersection Observer for animations
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsVisible(prev => ({
                            ...prev,
                            [entry.target.id]: true
                        }));
                    }
                });
            },
            { threshold: 0.1 }
        );

        sectionRefs.current.forEach((ref) => {
            if (ref) observer.observe(ref);
        });

        return () => observer.disconnect();
    }, []);

    // Animated stats
    useEffect(() => {
        const duration = 2000;
        const steps = 60;
        const interval = duration / steps;

        let currentStep = 0;
        const timer = setInterval(() => {
            currentStep++;
            const progress = currentStep / steps;
            const easeOut = 1 - Math.pow(1 - progress, 4);

            setAnimatedStats({
                processingTime: Math.round(15 * easeOut),
                successRate: Number((99.8 * easeOut).toFixed(1)),
                dailyPayouts: Math.round(50000 * easeOut),
                merchants: Math.round(25000 * easeOut)
            });

            if (currentStep >= steps) {
                clearInterval(timer);
            }
        }, interval);

        return () => clearInterval(timer);
    }, []);



    useEffect(() => {
        setIsVisible(true);
        const interval = setInterval(() => {
            setCurrentTitle((prev) => (prev + 1) % titles.length);
        }, 3000);

        return () => clearInterval(interval);
    }, []);








    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
                    }
                });
            },
            { threshold: 0.12 }
        );
        sectionRefs.current.forEach((ref) => ref && observer.observe(ref));
        return () => observer.disconnect();
    }, []);
    const reveal = (id, base = "") =>
        `${base} transition-all duration-700 ease-out will-change-transform will-change-opacity ${isVisible[id] ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-[0.98]'
        } motion-reduce:transition-none motion-reduce:translate-y-0 motion-reduce:opacity-100`;

    const features = [
        {
            icon: <Users className="w-8 h-8" />,
            title: "User & Role Management",
            desc: "Role-based access, maker-checker approvals",
            color: "indigo"
        },
        {
            icon: <Lock className="w-8 h-8" />,
            title: "Security & Compliance",
            desc: "PCI-DSS ready with audit logs",
            color: "red"
        },
        {
            icon: <Scale className="w-8 h-8" />,
            title: "Regulatory Compliance",
            desc: "Aligned with NPCI & NHAI guidelines",
            color: "yellow"
        },
        {
            icon: <LayoutDashboard className="w-8 h-8" />,
            title: "Unified Dashboard",
            desc: "Manage issuing, acquiring, disputes, and settlements",
            color: "purple"
        },
        {
            icon: <Plug className="w-8 h-8" />,
            title: "APIs & Integrations",
            desc: "Plug-and-play with CBS, PSPs, NPCI, and Toll Plaza systems",
            color: "lime"
        }
    ];


    const payoutCapabilities = [
        {
            id: 1,
            title: "Toll Plaza Integration",
            description: "Connect plaza systems with NPCI NETC switch.",
            icon: <Map className="w-8 h-8 text-white" />,
            bgColor: "from-indigo-500 to-indigo-600",
            iconBg: "bg-indigo-500"
        },
        {
            id: 2,
            title: "Real-Time Transaction Processing",
            description: "Faster toll deductions & barrier control.",
            icon: <Activity className="w-8 h-8 text-white" />,
            bgColor: "from-blue-600 to-blue-700",
            iconBg: "bg-blue-600"
        },
        {
            id: 3,
            title: "Settlement Management",
            description: "Automated daily settlement with NPCI & NHAI.",
            icon: <Wallet className="w-8 h-8 text-white" />,
            bgColor: "from-amber-600 to-amber-700",
            iconBg: "bg-amber-600"
        },
        {
            id: 4,
            title: "Dispute & Chargeback Handling",
            description: "Manage failed, duplicate, or incorrect transactions.",
            icon: <AlertTriangle className="w-8 h-8 text-white" />,
            bgColor: "from-rose-500 to-rose-600",
            iconBg: "bg-rose-500"
        },
        {
            id: 5,
            title: "Fraud & Risk Management (FRM)",
            description: "Detect misuse, duplicate tags & blocked accounts.",
            icon: <ShieldAlert className="w-8 h-8 text-white" />,
            bgColor: "from-red-600 to-red-700",
            iconBg: "bg-red-600"
        },
        {
            id: 6,
            title: "MIS & Analytics",
            description: "Toll collection trends, lane-level analysis, traffic flow insights.",
            icon: <BarChart4 className="w-8 h-8 text-white" />,
            bgColor: "from-slate-600 to-slate-700",
            iconBg: "bg-slate-600"
        },
        {
            id: 7,
            title: "Integration Ready",
            description: "Compatible with NPCI switch, banks, and acquirer systems.",
            icon: <Plug className="w-8 h-8 text-white" />,
            bgColor: "from-lime-500 to-lime-600",
            iconBg: "bg-lime-500"
        }
    ];


    const payoutTitles = [
        "NETC ACQUIRING SOLUTION(FOR TOLL PLAZAS & OPERATORS)",
        // "INSTANT TRANSFERS",
        // "BULK PAYMENTS",
        // "SMART DISBURSEMENTS"
    ];

    const payoutDescriptions = [
        "Enabling toll operators, acquirers & banks to manage toll transactions with speed & accuracy.",
        // "Experience lightning-fast fund transfers with 24x7 availability and multi-channel payment options for seamless business operations.",
        // "Process thousands of payments simultaneously with automated bulk upload capabilities and intelligent workflow management.",
        // "Optimize your disbursement operations with automated reconciliation, maker-checker approvals, and real-time tracking systems."
    ];

    useEffect(() => {
        setIsVisible(true);

        const titleInterval = setInterval(() => {
            setCurrentTitleIndex((prev) => (prev + 1) % payoutTitles.length);
        }, 4000);

        return () => clearInterval(titleInterval);
    }, []);


    const titles = [
        "NETC ISSUING SOLUTIONS (FOR BANKS & PSPs)",
        // "SEAMLESS PAYMENTS",
        // "SECURE TRANSACTIONS",
        // "SMART COLLECTIONS"
    ];

    const descriptions = [
        "Enabling banks, fintechs, and issuers to onboard and manage FASTag customers efficiently.",
        // "Experience the future of payment processing with advanced AI-driven routing and omnichannel acceptance capabilities.",
        // "Protect your business with enterprise-grade security, fraud detection, and real-time monitoring systems.",
        // "Optimize your revenue collection with intelligent routing, automated reconciliation, and instant settlement solutions."
    ];



    const capabilities = [
        {
            id: 1,
            title: "Customer Onboarding & eKYC",
            description: "Digital KYC with Aadhaar, PAN, RC verification.",
            icon: <UserPlus className="w-8 h-8 text-white" />,
            bgColor: "from-indigo-500 to-indigo-600",
            iconBg: "bg-indigo-500"
        },
        {
            id: 2,
            title: "FASTag Issuance & Mapping",
            description: "Link tags to vehicle RC & customer profile.",
            icon: <Link2 className="w-8 h-8 text-white" />,
            bgColor: "from-cyan-500 to-cyan-600",
            iconBg: "bg-cyan-500"
        },
        {
            id: 3,
            title: "Recharge & Top-Up",
            description: "Multi-channel (UPI, Net Banking, Cards, Wallets).",
            icon: <BatteryCharging className="w-8 h-8 text-white" />,
            bgColor: "from-pink-500 to-pink-600",
            iconBg: "bg-pink-500"
        },
        {
            id: 4,
            title: "Alerts & Notifications",
            description: "SMS, WhatsApp & App notifications for usage, recharge & low balance.",
            icon: <Bell className="w-8 h-8 text-white" />,
            bgColor: "from-yellow-500 to-yellow-600",
            iconBg: "bg-yellow-500"
        },
        {
            id: 5,
            title: "Blacklist & Dispute Handling",
            description: "Manage blacklist requests & customer disputes in real time.",
            icon: <Ban className="w-8 h-8 text-white" />,
            bgColor: "from-red-500 to-red-600",
            iconBg: "bg-red-500"
        },
        {
            id: 6,
            title: "MIS & Reporting",
            description: "Customer lifecycle reports, recharge trends, settlement summaries.",
            icon: <BarChart2 className="w-8 h-8 text-white" />,
            bgColor: "from-slate-500 to-slate-600",
            iconBg: "bg-slate-500"
        },
        {
            id: 7,
            title: "Integration Ready",
            description: "NPCI systems, Core Banking, CBS, PSP APIs.",
            icon: <PlugZap className="w-8 h-8 text-white" />,
            bgColor: "from-lime-500 to-lime-600",
            iconBg: "bg-lime-500"
        }
    ];





    const PanelCard = ({ title, subtitle, features, bgGradient }) => (
        <div className={`rounded-xl ${bgGradient} p-8 text-white shadow-xl transform hover:scale-105 transition-all duration-300`}>
            <div className="mb-6">
                <h2 className="text-3xl font-bold mb-2">{title}</h2>
                <p className="text-lg opacity-90">{subtitle}</p>
            </div>

            <div className="space-y-4">
                {features.map((feature, index) => {
                    const IconComponent = feature.icon;
                    return (
                        <div key={index} className="flex items-start space-x-3 bg-white/10 rounded-lg p-3 backdrop-blur-sm">
                            <IconComponent className="w-6 h-6 mt-1 flex-shrink-0" />
                            <span className="text-base leading-relaxed">{feature.text}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-white">
            {/* Hero Section - Keep unchanged as requested */}
            <Header />

            <section
                className="relative pt-12 pb-12 px-4 sm:px-6 lg:px-8 min-h-[70vh] flex items-center overflow-hidden"
                style={{
                    background: `linear-gradient(135deg, 
          rgba(59, 130, 246, 0.1) 0%, 
          rgba(147, 51, 234, 0.1) 50%, 
          rgba(219, 39, 119, 0.1) 100%
        )`,
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                }}
            >
                {/* Animated Background Elements */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div
                        className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-blue-200/30 to-purple-200/30 rounded-full animate-pulse transform rotate-45"
                        style={{ transform: `translateY(${scrollY * 0.1}px) rotate(45deg)` }}
                    ></div>
                    <div
                        className="absolute top-40 right-20 w-24 h-24 bg-gradient-to-br from-indigo-200/40 to-blue-200/40 rounded-lg animate-bounce transform rotate-12"
                        style={{ animationDelay: '1s', transform: `translateY(${scrollY * 0.15}px) rotate(12deg)` }}
                    ></div>
                    <div
                        className="absolute bottom-20 left-20 w-16 h-16 bg-gradient-to-br from-purple-200/30 to-pink-200/30 rounded-full animate-pulse transform -rotate-12"
                        style={{ animationDelay: '2s', transform: `translateY(${scrollY * 0.2}px) rotate(-12deg)` }}
                    ></div>
                    <div
                        className="absolute bottom-40 right-10 w-20 h-20 bg-gradient-to-br from-cyan-200/40 to-blue-200/40 rounded-lg animate-bounce transform rotate-45"
                        style={{ animationDelay: '0.5s', transform: `translateY(${scrollY * 0.12}px) rotate(45deg)` }}
                    ></div>
                </div>

                <div className="max-w-7xl mx-auto relative z-10">
                    <div className="grid lg:grid-cols-2 gap-8 items-center">
                        {/* Left Content */}
                        <div className="space-y-6">
                            <div className="space-y-4">
                                {/* <div className="flex justify-start">
                                    <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
                                        <Star className="w-4 h-4" />
                                        <div className="text-center leading-tight">
                                            Pay-In & Pay-Out Solutions
                                            All in One Platform
                                        </div>
                                    </div>
                                </div> */}
                                <div className="flex justify-start">
                                    <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-1.5 rounded-full text-xs sm:text-sm font-medium shadow-md max-w-full">
                                        <Star className="w-3 h-3 flex-shrink-0" aria-hidden="true" />
                                        <p className="whitespace-normal leading-tight">
                                            NETC Solutions — Smart digital solutions for efficient business operations.
                                        </p>
                                    </div>
                                </div>



                                {/* <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight tracking-tight">
                                    <div className="text-gray-800 font-semibold">
                                        Payment Gateway Suite
                                    </div>
                                    <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent">
                                        Simplify your payments Amplify your growth
                                    </div>

                                </h1> */}

                                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight tracking-tight">
                                    <div className="text-gray-800 font-semibold">
                                        NETC SOLUTIONS
                                    </div>
                                    <div className="text-lg sm:text-xl lg:text-1.5xl font-medium bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mt-1">
                                        Smart FASTag Issuing & Acquiring for the Digital Tolling Ecosystem
                                    </div>
                                </h1>


                                <p className="text-lg text-gray-600 leading-relaxed font-normal">
                                    The future of tolling is fast, seamless & cashless. Our NETC Solutions empower banks, PSPs, acquirers, and toll operators to manage the end-to-end FASTag lifecycle — from issuing tags to vehicles to processing toll transactions at plazas.
                                    We ensure compliance with NPCI & NHAI guidelines, while delivering real-time settlements, fraud prevention, and a smooth commuter experience.                                </p>
                            </div>
                        </div>

                        {/* Right Content - Compact Video Player */}
                        <div className="relative">
                            <div className="relative rounded-xl overflow-hidden shadow-2xl bg-black/20 backdrop-blur-sm border border-white/10">
                                <video
                                    className="w-full h-auto rounded-xl"
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                    src="./transport.mp4"
                                    preload="metadata"
                                >
                                    <source src="/path/to/your/video.mp4" type="video/mp4" />
                                    <source src="/path/to/your/video.webm" type="video/webm" />
                                    Your browser does not support the video tag.
                                </video>

                                {/* Optional overlay for styling */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none rounded-xl"></div>
                            </div>

                            {/* Decorative elements */}
                            <div className="absolute -top-4 -right-4 w-20 h-20 bg-purple-400/30 rounded-full blur-xl"></div>
                            <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-blue-400/30 rounded-full blur-lg"></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* First Section */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 py-16 px-6 sm:px-8">
                <style dangerouslySetInnerHTML={{
                    __html: `
      /* Animations remain same */
      @keyframes flipIn {
        0% { opacity: 0; transform: rotateY(-90deg) translateY(-20px); }
        50% { opacity: 0.5; transform: rotateY(-45deg) translateY(-10px); }
        100% { opacity: 1; transform: rotateY(0deg) translateY(0px); }
      }
      @keyframes slideInUp {
        0% { opacity: 0; transform: translateY(50px) scale(0.9); }
        100% { opacity: 1; transform: translateY(0px) scale(1); }
      }
      @keyframes fadeIn {
        0% { opacity: 0; transform: translateY(20px); }
        100% { opacity: 1; transform: translateY(0px); }
      }
      @keyframes pulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.02); }
      }
      .flip-animation { animation: flipIn 0.8s ease-out; }
      .slide-up-animation { animation: slideInUp 0.6s ease-out forwards; opacity: 0; }
      .fade-in-animation { animation: fadeIn 0.6s ease-out forwards; opacity: 0; }
      .pulse-animation { animation: pulse 2s infinite; }
      .card-hover:hover { transform: translateY(-10px) scale(1.02); }
      .icon-hover:hover { transform: scale(1.1) rotate(5deg); }
      .text-gradient {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }
    `
                }} />

                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="text-center mb-12">
                        <h1
                            key={currentTitle}
                            className="text-3xl sm:text-4xl font-bold tracking-tight text-gradient flip-animation"
                        >
                            {titles[currentTitle]}
                        </h1>
                        <p
                            key={`desc-${currentTitle}`}
                            className={`mt-4 text-gray-600 text-base max-w-2xl mx-auto leading-relaxed ${isVisible ? 'fade-in-animation' : 'opacity-0'}`}
                            style={{ animationDelay: '0.3s' }}
                        >
                            {descriptions[currentTitle]}
                        </p>
                    </div>

                    {/* Cards Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 px-4 sm:px-6 lg:px-8">
                        {capabilities.map((capability, index) => (
                            <div
                                key={capability.id}
                                className={`group bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-500 border border-gray-100 card-hover ${isVisible ? 'slide-up-animation' : 'opacity-0'
                                    }`}
                                style={{ animationDelay: `${index * 150 + 600}ms` }}
                            >
                                {/* Icon */}
                                <div className="flex justify-center mt-0 sm:-mt-8">
                                    <div
                                        className={`${capability.iconBg} rounded-2xl p-4 shadow-md icon-hover`}
                                    >
                                        {capability.icon}
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="p-6 pt-4 text-center">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-2 group-hover:text-gray-900 transition">
                                        {capability.title}
                                    </h3>
                                    <p className="text-gray-600 text-sm leading-relaxed">
                                        {capability.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                </div>
            </div>

            {/* Second Section */}
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 py-16 px-6 sm:px-8">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="text-center mb-12">
                        <h1
                            key={currentTitleIndex}
                            className="text-3xl sm:text-4xl font-bold tracking-tight text-gradient flip-animation"
                        >
                            {payoutTitles[currentTitleIndex]}
                        </h1>
                        <p
                            key={`desc-${currentTitleIndex}`}
                            className={`mt-4 text-slate-600 text-base max-w-2xl mx-auto leading-relaxed ${isVisible ? 'fade-in-animation' : 'opacity-0'}`}
                            style={{ animationDelay: '0.3s' }}
                        >
                            {payoutDescriptions[currentTitleIndex]}
                        </p>
                    </div>

                    {/* Cards Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 px-4 sm:px-6 lg:px-8">
                        {payoutCapabilities.map((capability, index) => (
                            <div
                                key={capability.id}
                                className={`group bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-500 border border-slate-100 card-hover ${isVisible ? 'slide-up-animation' : 'opacity-0'
                                    }`}
                                style={{ animationDelay: `${index * 150 + 600}ms` }}
                            >
                                {/* Icon */}
                                <div className="flex justify-center mt-0 sm:-mt-8">
                                    <div className={`${capability.iconBg} rounded-2xl p-4 shadow-md icon-hover`}>
                                        {capability.icon}
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="p-6 pt-4 text-center">
                                    <h3 className="text-lg font-semibold text-slate-800 mb-2 group-hover:text-slate-900 transition">
                                        {capability.title}
                                    </h3>
                                    <p className="text-slate-600 text-sm leading-relaxed">
                                        {capability.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                </div>
            </div>

            <div className="py-16 px-4 bg-slate-50">
                <div className="max-w-6xl mx-auto text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 animate-fade-in-up">
                        <span className="inline-block animate-pulse bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600 bg-clip-text text-transparent">
                            ADVANCED CAPABILITIES
                        </span>{" "}
                        {/* <span className="inline-block animate-bounce-slow">

                            Payment Gateway Suite?
                        </span> */}
                    </h2>

                    <style jsx>{`
                    @keyframes fade-in-up {
                        0% {
                            opacity: 0;
                            transform: translateY(20px);
                        }
                        100% {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }

                    @keyframes bounce-slow {
                        0%, 20%, 50%, 80%, 100% {
                            transform: translateY(0);
                        }
                        40% {
                            transform: translateY(-3px);
                        }
                        60% {
                            transform: translateY(-1px);
                        }
                    }

                    .animate-fade-in-up {
                        animation: fade-in-up 1s ease-out;
                    }

                    .animate-bounce-slow {
                        animation: bounce-slow 2s infinite;
                        animation-delay: 0.5s;
                    }
                `}</style>

                    <div className="grid md:grid-cols-5 gap-6 mt-12">
                        {features.map((feature, index) => (
                            <div
                                key={index}
                                className="p-6 bg-white border border-gray-200 rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center group hover:scale-[1.02] transition-transform"
                            >
                                <div className={`mb-4 p-4 rounded-full bg-${feature.color}-100 text-${feature.color}-600`}>
                                    {feature.icon}
                                </div>
                                <h3 className="text-base font-semibold text-slate-800 mb-2">{feature.title}</h3>
                                <p className="text-sm text-slate-600 leading-relaxed">{feature.desc}</p>
                            </div>
                        ))}
                    </div>

                </div>
            </div>

            <section className="py-8 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 relative overflow-hidden">
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full animate-pulse"></div>
                    <div className="absolute top-40 right-20 w-24 h-24 bg-white/5 rounded-lg animate-bounce transform rotate-45" style={{ animationDelay: '1s' }}></div>
                    <div className="absolute bottom-20 left-20 w-16 h-16 bg-white/10 rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
                    <div className="absolute bottom-40 right-10 w-20 h-20 bg-white/5 rounded-lg animate-bounce transform -rotate-12" style={{ animationDelay: '0.5s' }}></div>

                    <div className="absolute top-1/4 left-1/4 animate-float">
                        <Zap className="w-8 h-8 text-white/20" />
                    </div>
                    <div className="absolute top-1/3 right-1/3 animate-float" style={{ animationDelay: '1.5s' }}>
                        <Shield className="w-6 h-6 text-white/20" />
                    </div>
                    <div className="absolute bottom-1/3 left-1/3 animate-float" style={{ animationDelay: '3s' }}>
                        <TrendingUp className="w-7 h-7 text-white/20" />
                    </div>
                </div>

                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
                    {/* Main Content */}


                    <div className="space-y-4 animate-fadeInUp">
                        <h2 className="text-2xl sm:text-3xl lg:text-3xl font-bold text-white leading-tight animate-slideInLeft">
                            End-to-end NETC solution for FASTag issuance and toll collection.
                        </h2>
                    </div>

                    {/* CTA Button - Smaller and More Compact */}
                    <div className="mt-6 animate-fadeInUp" style={{ animationDelay: '1.2s' }}>
                        <button
                            className="group relative inline-flex items-center gap-3 bg-gradient-to-r from-white to-blue-50 text-blue-700 px-8 py-3 rounded-xl font-semibold text-lg hover:from-yellow-300 hover:to-orange-300 hover:text-gray-900 transform hover:scale-105 transition-all duration-500 shadow-xl hover:shadow-yellow-300/50"
                            onClick={() => {
                                window.location.href = '/contact';
                            }}
                        >
                            <Play className="w-5 h-5 group-hover:animate-bounce" />
                            <span>Get In Touch</span>
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />

                            {/* Button Glow Effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                        </button>
                    </div>
                </div>

                <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-10px) rotate(5deg); }
          66% { transform: translateY(5px) rotate(-5deg); }
        }
        
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-50px); }
          to { opacity: 1; transform: translateX(0); }
        }
        
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(50px); }
          to { opacity: 1; transform: translateX(0); }
        }
        
        @keyframes slideInUp {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 0.8s ease-out forwards;
          opacity: 0;
        }
        
        .animate-slideInLeft {
          animation: slideInLeft 0.8s ease-out forwards;
          opacity: 0;
        }
        
        .animate-slideInRight {
          animation: slideInRight 0.8s ease-out forwards;
          opacity: 0;
        }
        
        .animate-slideInUp {
          animation: slideInUp 0.8s ease-out forwards;
          opacity: 0;
        }
      `}</style>
            </section>
            <Footer />
        </div>
    );
}

